import { FC, useCallback, useEffect } from 'react'
import { UfinetActionButton, UfinetInput, UfinetSelect, IUfinetSelectOption, colorLighten } from 'ufinet-web-components'
import { onFormikTextChanges } from 'ufinet-web-functions'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import {
	editRow,
	addRow,
	getOperatorOptions,
	getStatusOptions,
	getPhysicalNetworkForOperatorOptions,
	getCtoModelOptions,
} from 'api/requests'
import { Translation, useTranslation } from 'utils/translation/Translation'
import { ExcludedColumns } from 'types'

const positiveNumberRangeErrorText = 'MODALS.POSITIVENUMBERRANGEERROR'

type NewAndEditCtoModalProps = {
	hideModal: Function
	apiRoutePath: string
	row?: any
	successFn: Function
	inputs?: any
	toInvalidateQuery: string
	onSuccessAddMessage: string
	onSuccessEditMessage: string
	onErrorGenericMessage: string
}

const NewModal: FC<NewAndEditCtoModalProps> = ({
	hideModal,
	apiRoutePath,
	row,
	successFn,
	inputs,
	toInvalidateQuery,
	onSuccessAddMessage,
	onSuccessEditMessage,
	onErrorGenericMessage,
}) => {
	const queryClient = useQueryClient()
	const translate = useTranslation()
	const positiveNumberRangeError = translate(positiveNumberRangeErrorText)


	const excludedColumns = Object.values(ExcludedColumns) as string[]

	const filteredInputs = inputs.filter((col: any) => !excludedColumns.includes(col.columnName) || col.showInModalEdit)

	const mutation = useMutation({
		mutationFn: (values: any) =>
			Object.keys(row).length ? editRow(values, apiRoutePath) : addRow(values, apiRoutePath),
		onSuccess: ({ data }) => {
			queryClient.invalidateQueries({ queryKey: [toInvalidateQuery] })
			hideModal()
			successFn && successFn()
			row ? toast.success(data?.message || onSuccessEditMessage) : toast.success(data?.message)
		},
		onError: (error: any) => {
			toast.error(
				error?.response?.data?.message ||
					error?.response?.data?.detail ||
					error?.response?.data?.title ||
					onErrorGenericMessage
			)
		},
	})

	const operatorOptionsQuery = useQuery({
		queryKey: [`${apiRoutePath}/filterStatus`],
		queryFn: () => getOperatorOptions(),
		enabled: inputs.some((input: any) => input.selectableOptions === 'operatorCode'),
	})
	const { data: operatorOptions } = operatorOptionsQuery

	const physicalNetworkForOperatorQuery = useQuery({
		queryKey: [`${apiRoutePath}/physicalNetworkForOperatorOptions`],
		queryFn: () => getPhysicalNetworkForOperatorOptions(),
		enabled: inputs.some((input: any) => input.selectableOptions === 'physicalNetworkForOperator'),
	})

	const { data: physicalNetworkForOperatorOptions } = physicalNetworkForOperatorQuery

	// Do query only when inputs has "status" column
	const statusOptionsQuery = useQuery({
		queryKey: ['statusOptions'],
		queryFn: () => getStatusOptions(`${apiRoutePath}/filterStatus`),
		// enabled when inputs has "status" column and apiRoutePath changes
		enabled: inputs.some((input: any) => input.selectableOptions === 'status'),
		// Refetch when when component mounts
		refetchOnMount: true,
	})
	const { data: statusOptions } = statusOptionsQuery

	const ctoModelOptionsQuery = useQuery({
		queryKey: ['ctoModelOptions'],
		queryFn: () => getCtoModelOptions(`${apiRoutePath}/filterModel`),
		enabled: inputs.some((input: any) => input.selectableOptions === 'ctoModel'),
		refetchOnMount: true,
	})
	const { data: ctoModelOptions } = ctoModelOptionsQuery

	const polygonTypeOptions = [
		{ label: 'SDU', value: 'SDU' },
		{ label: 'MDU', value: 'MDU' },
	]

	const maxCtoPortCtob = [
		{ label: '8', value: 8 },
		{ label: '16', value: 16 },
		{ label: '5000', value: 5000 },
	]

	useEffect(() => {
		return () => {
			formik.resetForm()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const { mutateAsync: mutate, isPending } = mutation

	// Function to generate the Yup validation schema
	const generateColumnsSchema = (columns: any) => {
		const schemaShape: any = {}
		columns.forEach((column: any) => {
			if (column.isMultiSelect && column.isFieldRequired) {
				// For multi-select fields, enforce array of strings with at least one selection
				schemaShape[column.columnName] = Yup.array()
					.of(Yup.string())
					.min(1, Translation(column.requiredFieldErrorText))
			} else if (column.columnType === 'string' && column.isFieldRequired) {
				schemaShape[column.columnName] = Yup.string()
					.max(column.maxLength || Infinity, column.maxLengthErrorText ? Translation(column.maxLengthErrorText) : '')
					.required(Translation(column.requiredFieldErrorText))
			}
			if (column.columnType === 'number' && column.isFieldRequired) {
				let numSchema = Yup.number().required(Translation(column.requiredFieldErrorText))
				if (column.min !== undefined) {
					numSchema = numSchema.min(column.min, Translation(column.minErrorText).replace('{min}', column.min))
				}
				if (column.max !== undefined) {
					numSchema = numSchema.max(column.max, Translation(column.maxErrorText).replace('{max}', column.max))
				}
				if (column.columnName === 'maxCtoPort') {
					numSchema = numSchema
						.integer(positiveNumberRangeError)
						.min(1, positiveNumberRangeError)
						.max(5000, positiveNumberRangeError)
				}
				schemaShape[column.columnName] = numSchema
			}
		})

		return Yup.object().shape(schemaShape)
	}

	// Function to generate the Formik initial values
	const generateInitialValues = (columns: any, cto: string) => {
		const schemaShape: any = {}
		columns.forEach((column: any, index: number) => {
			const existingValue = cto[column.columnName]
			if (column.isMultiSelect) {
				schemaShape[column.columnName] = Array.isArray(existingValue)
					? existingValue
					: existingValue
						? [existingValue]
						: []
			} else {
				schemaShape[column.columnName] = existingValue || ''
			}
		})

		return schemaShape
	}

	const dataFormSchema = generateColumnsSchema(filteredInputs)

	const formik = useFormik({
		initialValues: generateInitialValues(filteredInputs, row),
		validationSchema: dataFormSchema,
		onSubmit: (values) => (row ? mutate(values) : mutate(values)),
		validateOnChange: false,
		validateOnBlur: false,
	})

	const onTextChange = useCallback(onFormikTextChanges, [])
	const onPositiveNumberChange = useCallback(
		(formik: any, field: string) => (e: any) => {
			const value = e?.target?.value ?? e ?? ''

			if (value === '') {
				formik.setFieldValue(field, '')
				formik.setFieldError(field, undefined)
				return
			}

			if (!/^[1-9]\d*$/.test(String(value))) {
				formik.setFieldError(field, positiveNumberRangeError)
				return
			}

			if (Number(value) < 1 || Number(value) > 5000) {
				formik.setFieldError(field, positiveNumberRangeError)
				return
			}

			formik.setFieldValue(field, value)
			formik.setFieldError(field, undefined)
		},
		[positiveNumberRangeError]
	)

	type MyFormikErrors = {
		[key in typeof inputs[number]['columnName']]?: string
	}


	return (
		<form onSubmit={formik.handleSubmit} className="container p-15 h-100 d-flex flex-column justify-content-center">
			<div className="row pb-10">
				{filteredInputs.map((column: any, index: number) =>
					column.selectableOptions === 'operatorCode' ? (
						<UfinetSelect
							labelTitle={Translation(column.columnLabelIntl)}
							className="col-4"
							options={operatorOptions}
							requiredIcon={column.isFieldRequired}
							error={(formik.errors as MyFormikErrors)[column.columnName]}
							onChange={(value: any) => {
								formik.setFieldValue(column.columnName, value.value)
							}}
							value={
								{
									label: formik.values?.operator_Code || formik.values?.operatorCode || '',
									value: formik.values?.operator_Code || formik.values?.operatorCode || '',
								} as IUfinetSelectOption
							}
						/>
					) : column.selectableOptions === 'status' ? (
						<UfinetSelect
							labelTitle={Translation(column.columnLabelIntl)}
							className="col-4"
							options={statusOptions}
							requiredIcon={column.isFieldRequired}
							error={(formik.errors as MyFormikErrors)[column.columnName]}
							onChange={(value: any) => {
								formik.setFieldValue(column.columnName, value.value)
							}}
							value={
								{
									label: formik.values.status,
									value: formik.values.status,
								} as IUfinetSelectOption
							}
						/>
					) : column.selectableOptions === 'polygonType' ? (
						<UfinetSelect
							labelTitle={Translation(column.columnLabelIntl)}
							className="col-4"
							options={polygonTypeOptions}
							requiredIcon={column.isFieldRequired}
							error={(formik.errors as MyFormikErrors)[column.columnName]}
							onChange={(value: any) => {
								formik.setFieldValue(column.columnName, value.value)
							}}
							value={
								{
									label: formik.values.type,
									value: formik.values.type,
								} as IUfinetSelectOption
							}
						/>
					) : column.selectableOptions === 'physicalNetworkForOperator' ? (
						<UfinetSelect
							labelTitle={Translation(column.columnLabelIntl)}
							className="col-4"
							isMulti={column.isMultiSelect}
							options={physicalNetworkForOperatorOptions}
							requiredIcon={column.isFieldRequired}
							error={(formik.errors as MyFormikErrors)[column.columnName]}
							onChange={(value: any) => {
								if (column.isMultiSelect) {
									// Handle multiple selection - value is an array of selected options
									const selectedValues = value ? value.map((option: IUfinetSelectOption) => option.value) : []
									formik.setFieldValue(column.columnName, selectedValues)
								} else {
									// Handle single selection - value is a single option
									formik.setFieldValue(column.columnName, value ? value.value : '')
								}
							}}
							value={
								column.isMultiSelect
									? // For multi-select, convert array of values to array of options
									  Array.isArray(formik.values[column.columnName])
											? formik.values[column.columnName].map((val: string) => ({
													label: val,
													value: val,
											  }))
											: []
									: // For single select, convert single value to option
									  {
											label: formik.values[column.columnName] || '',
											value: formik.values[column.columnName] || '',
									  }
							}
						/>
					) : column.selectableOptions === 'ctoModel' ? (
						<UfinetSelect
							labelTitle={Translation(column.columnLabelIntl)}
							className="col-4"
							options={ctoModelOptions}
							requiredIcon={column.isFieldRequired}
							error={(formik.errors as MyFormikErrors)[column.columnName]}
							onChange={(value: any) => {
								console.log({ value, column })

								console.log({ value: formik.values })
								formik.setFieldValue(column.columnName, value.value)
							}}
							value={
								{
									label: formik.values.ctO_MODEL,
									value: formik.values.ctO_MODEL,
								} as IUfinetSelectOption
							}
						/>
					) : column.columnName === 'maxCtoPort' ? (			
						<>

						<UfinetInput
							key={column.columnName}
							requiredIcon={column.isFieldRequired}
							error={(formik.errors as MyFormikErrors)[column.columnName]}
							type='number'
							labelTitle={Translation(column.columnLabelIntl)}
							className="col-4"
							isDisabled={
								(index === 0 && Object.keys(row).length !== 0) ||
								(column.isFieldEditDisabled && Object.keys(row).length !== 0)
							}
							onChange={onPositiveNumberChange(formik, column.columnName)}
							value={formik.values[column.columnName as keyof typeof formik.values] || ''}
						/>
						</>			
					) :	column.dontShowInTableAndModal ? (
						<></>
					) : (
						<UfinetInput
							key={column.columnName}
							requiredIcon={column.isFieldRequired}
							error={(formik.errors as MyFormikErrors)[column.columnName]}
							type={column.columnType === 'string' ? 'text' : 'number'}
							labelTitle={Translation(column.columnLabelIntl)}
							className="col-4"
							isDisabled={
								(index === 0 && Object.keys(row).length !== 0) ||
								(column.isFieldEditDisabled && Object.keys(row).length !== 0)
							}
							onChange={onTextChange(formik, column.columnName)}
							value={formik.values[column.columnName as keyof typeof formik.values] || ''}
						/>
					)
				)}
			</div>
			<UfinetActionButton isDisabled={isPending} className="mt-10" content={Translation('SAVE')} />
		</form>
	)
}

export default NewModal
